import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";

import {
  AI_AGENT_ADDON_SLUGS,
  AI_RECEPTIONIST_AGENT_SLUG,
  AI_SALE_AGENT_SLUG,
  getAddonValidTill,
  getBillingCycleLabel,
  getBillingCycleName,
  getSupersededAiAddonIds,
  getUpfrontBillingDays,
  isAIAgentCycleDisabled,
} from "./aiAddonBilling";

/**
 * An AI add-on the org holds but whose validity has run out. There is no EXPIRED
 * status on subscription_addons — the row stays ACTIVE and only `valid_till`
 * moves — so lapsed add-ons look identical to live ones and would otherwise be
 * hidden from the picker, leaving CX no way to renew one alongside a new purchase.
 */
const isExpiredAiAddon = (addon) => {
  if (!AI_AGENT_ADDON_SLUGS.includes(addon?.slug)) return false;
  const validTill = getAddonValidTill(addon);
  if (!validTill) return false;
  return new Date(validTill).getTime() < Date.now();
};

// The v3 unit add-ons, bought as one add-on × quantity. In the V3 order flow these are the only
// extra-users / automation / lead-storage add-ons offered; the old v2 rows (users-v2-1,
// automation-v2-pack-of-5, lead-storage-extra-10k) stay in the catalog but are not shown.
const UNIT_ADDON_SLUGS = [
  "users-v3-1",
  "automation-v3-pack-of-5",
  "lead-storage-v3-extra-10k",
];

// Voice API add-ons: one plan_addons row per billing cycle (3 / 6 months), each bought × quantity.
// Rows are visible:false so customers don't see them; CX sells them from here.
const VOICE_API_SLUGS = [
  { slug: "voice-api-number", title: "VoiceAPI: Number", unit: "number" },
  { slug: "voice-api-channel", title: "VoiceAPI: Channel", unit: "channel" },
];
// Months one row covers, from plan_addons.billing_cycle (old rows: metadata flags).
const CYCLE_MONTHS = { monthly: 1, quarterly: 3, half_yearly: 6, yearly: 12 };
const cycleMonths = (addon) =>
  CYCLE_MONTHS[addon?.billing_cycle] ??
  (getUpfrontBillingDays(addon) ?? 30) / 30;

const QuantityAddonCard = ({
  icon,
  title,
  price,
  priceSuffix,
  value,
  onIncrement,
  onDecrement,
  currencySymbol,
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      background: "#fff",
      border: "1px solid #e9ecef",
      borderRadius: 12,
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      padding: "32px 12px",
      position: "relative",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        width: "100%",
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <div style={{ fontSize: 24 }}>{icon}</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500, color: "#212529" }}>
            {title}
          </div>
          <div style={{ color: "#495057", marginTop: 4 }}>
            {currencySymbol}{" "}
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {price ? price / 100 : "-"}
            </span>
            <span style={{ fontSize: 14, color: "#adb5bd" }}>
              {" "}
              {priceSuffix}
            </span>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: "1px solid #000",
            borderRadius: 6,
            padding: "4px 8px",
          }}
        >
          <Button
            color="secondary"
            size="sm"
            style={{ marginRight: 8, minWidth: 28 }}
            onClick={onDecrement}
          >
            -
          </Button>
          <div style={{ width: 20, textAlign: "center" }}>{value}</div>
          <Button
            color="secondary"
            size="sm"
            style={{ minWidth: 28 }}
            onClick={onIncrement}
          >
            +
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const AddonsCommon = ({
  selectedAddons,
  setSelectedAddons,
  setNewCombinations,
  plan,
  activeAddons,
  type,
  remainingDays,
  currencySymbol = "₹",
  // Only callers that send `{ id, quantity }` (the V3 order modal) can sell unit add-ons; the old
  // screens send bare ids, so they keep the one-row-per-unit counters.
  supportsQuantity = false,
  // V3 ADDON_BUY: a held AI agent add-on is renewable before it expires too (the server stacks the
  // new cycle after the current one), like the Renew button in the customer app.
  renewHeldAi = false,
}) => {
  // states
  const [extraTeamMembers, setExtraTeamMembers] = useState(0);
  const [extraAutomations, setExtraAutomations] = useState(0);
  const [extraLeadStorage, setExtraLeadStorage] = useState(0);
  const [selectedRecordingId, setSelectedRecordingId] = useState(null);
  // Voice API slug → chosen duration row id (the cart only holds it once quantity > 0).
  const [voiceApiCycle, setVoiceApiCycle] = useState({});
  const renderedGroups = new Set();

  // initial state
  // Sync extra counts and recording from selectedSetupFeeAddons
  useEffect(() => {
    const userV2Group = selectedAddons?.find((a) => a.slug === "users-v2-1");
    const automationV2Group = selectedAddons?.find(
      (a) => a.slug === "automation-v2-pack-of-5",
    );
    const userV2Count = userV2Group?.items?.length ?? userV2Group?.count ?? 0;
    const automationV2Count =
      automationV2Group?.items?.length ?? automationV2Group?.count ?? 0;
    const leadStorageGroup = selectedAddons?.find(
      (a) => a.slug === "lead-storage-extra-10k",
    );
    const leadStorageCount =
      leadStorageGroup?.items?.length ?? leadStorageGroup?.count ?? 0;
    const selectedRecording = selectedAddons?.find((a) =>
      a.slug?.includes("recording"),
    );
    setExtraTeamMembers(userV2Count);
    setExtraAutomations(automationV2Count);
    setExtraLeadStorage(leadStorageCount);
    setSelectedRecordingId(selectedRecording?.id || null);
  }, [plan, selectedAddons]);

  function handleAddon(addon) {
    if (addon.deselect) {
      setSelectedAddons((prev) => prev.filter((a) => a.id !== addon.id));
    } else {
      setSelectedAddons((prev) => {
        const exists = prev.find((a) => a.id === addon.id);
        if (exists) {
          return prev.filter((a) => a.id !== addon.id);
        }
        return [...prev, addon];
      });
    }
  }

  // One selection per AI group
  function handleAIAgentAddonClick(addon) {
    setSelectedAddons((prev) => {
      const withoutSameSlug = prev.filter((a) => a.slug !== addon.slug);
      const exists = prev.find((a) => a.id === addon.id);

      if (exists) {
        return withoutSameSlug;
      }

      return [...withoutSameSlug, addon];
    });
  }

  // constants
  const currentPlanAddons = (plan?.addons || []).filter(
    (a) => a.visible === true,
  );
  // Unit add-ons of the plan, by slug (V3 order flow only).
  const unitAddons = supportsQuantity
    ? UNIT_ADDON_SLUGS.map((slug) =>
        currentPlanAddons.find((a) => a.slug === slug),
      ).filter(Boolean)
    : [];
  // The V3 flow never offers the old v2 counters; the old screens keep them.
  const hasUnitFor = () => supportsQuantity;
  const unitQuantity = (addon) =>
    Number(selectedAddons?.find((a) => a.id === addon.id)?.quantity) || 0;
  function changeUnitQuantity(addon, delta) {
    const max = Number(addon?.metadata?.max_quantity) || 1;
    setSelectedAddons((prev) => {
      const current = Number(prev.find((a) => a.id === addon.id)?.quantity) || 0;
      const next = Math.min(max, Math.max(0, current + delta));
      const others = prev.filter((a) => a.id !== addon.id);
      return next > 0 ? [...others, { ...addon, quantity: next }] : others;
    });
  }
  // Switching duration moves the picked quantity onto that cycle's row.
  function pickVoiceApiCycle(slug, row) {
    setVoiceApiCycle((prev) => ({ ...prev, [slug]: row.id }));
    setSelectedAddons((prev) => {
      const current = prev.find((a) => a.slug === slug);
      const others = prev.filter((a) => a.slug !== slug);
      return current ? [...others, { ...row, quantity: current.quantity }] : others;
    });
  }
  // Held add-ons are hidden from the picker, except an expired AI one (or, with
  // renewHeldAi, any held AI one): that is offered again so a renewal and a new
  // purchase go out as a single change with one payment id, instead of two
  // separate transactions.
  // Rows a newer variant of the same slug already replaced: history, not holdings.
  // Renewing one would re-buy the cycle the customer moved off.
  const supersededAiAddonIds = getSupersededAiAddonIds(activeAddons || []);
  const renewableAiAddonIds = (activeAddons || [])
    .filter(
      (a) =>
        !supersededAiAddonIds.includes(a.id) &&
        (isExpiredAiAddon(a) ||
          (renewHeldAi && AI_AGENT_ADDON_SLUGS.includes(a?.slug))),
    )
    .map((a) => a.id);
  const availableAddons = currentPlanAddons.filter(
    (a) =>
      renewableAiAddonIds.includes(a.id) ||
      !activeAddons.some((active) => active.id === a.id),
  );
  const filteredAddons = availableAddons?.filter(
    (addon) =>
      !addon.slug?.includes("users") &&
      !addon.slug?.includes("automation") &&
      !addon.slug?.includes("lead-storage") &&
      !VOICE_API_SLUGS.some((v) => v.slug === addon.slug),
  );
  const allV2Ids = currentPlanAddons.filter(
    (a) =>
      (a.slug && a.slug.includes("users-v2-1")) ||
      a.slug.includes("automation-v2-pack-of-5") ||
      a.slug.includes("lead-storage"),
  );

  let usedV2Ids = [];
  let availableV2Ids = [];
  let usedUserV2Ids = [];
  let usedAutomationV2Ids = [];
  let usedLeadStorageIds = [];

  if (type === "change-plan") {
    usedV2Ids = selectedAddons.filter(
      (a) =>
        (a.slug && a.slug.includes("users-v2-1")) ||
        a.slug.includes("automation-v2-pack-of-5") ||
        a.slug.includes("lead-storage"),
    );
    const usedIds = usedV2Ids.flatMap((group) =>
      Array.isArray(group.id) ? group.id : [group.id],
    );
    availableV2Ids = allV2Ids.filter((a) => !usedIds.includes(a.id));
    usedUserV2Ids =
      usedV2Ids.filter((addon) => addon.slug.includes("users-v2-1"))?.[0]
        ?.items || [];
    usedAutomationV2Ids =
      usedV2Ids.filter((addon) =>
        addon.slug.includes("automation-v2-pack-of-5"),
      )?.[0]?.items || [];
    usedLeadStorageIds =
      usedV2Ids.filter((addon) => addon.slug.includes("lead-storage"))?.[0]
        ?.items || [];
  } else {
    usedV2Ids = activeAddons.filter(
      (addon) =>
        (addon.slug && addon.slug.includes("users-v2-1")) ||
        addon.slug.includes("automation-v2-pack-of-5") ||
        addon.slug.includes("lead-storage"),
    );
    const usedIds = usedV2Ids.flatMap((group) =>
      Array.isArray(group.id) ? group.id : [group.id],
    );
    availableV2Ids = allV2Ids.filter((a) => !usedIds.includes(a.id));
    usedUserV2Ids = usedV2Ids.filter((addon) =>
      addon.slug.includes("users-v2-1"),
    );
    usedAutomationV2Ids = usedV2Ids.filter((addon) =>
      addon.slug.includes("automation-v2-pack-of-5"),
    );
    usedLeadStorageIds = usedV2Ids.filter((addon) =>
      addon.slug.includes("lead-storage"),
    );
  }

  const availableUserV2Ids = availableV2Ids.filter((addon) =>
    addon.slug.includes("users-v2-1"),
  );
  const availableAutomationV2Ids = availableV2Ids.filter((addon) =>
    addon.slug.includes("automation-v2-pack-of-5"),
  );
  const availableLeadStorageIds = availableV2Ids.filter((addon) =>
    addon.slug.includes("lead-storage"),
  );

  // addons helpers
  function handleAddonChange(addonType, action, availableList, setCount) {
    setNewCombinations((prev) => {
      const currentGroup = prev.find((item) => item.slug === addonType);
      const others = prev.filter((item) => item.slug !== addonType);

      const usedIdsByType =
        addonType === "users-v2-1"
          ? usedUserV2Ids
          : addonType === "automation-v2-pack-of-5"
            ? usedAutomationV2Ids
            : addonType === "lead-storage-extra-10k"
              ? usedLeadStorageIds
              : [];

      let selectedItems = currentGroup?.items || [];
      let nextSelectedItems = [...selectedItems];

      if (action === "increment") {
        const alreadySelectedIds = selectedItems.map((a) => a.id);

        const next = availableList.find(
          (a) =>
            a.slug &&
            a.slug.startsWith(addonType) &&
            !alreadySelectedIds.includes(a.id),
        );

        if (next) {
          nextSelectedItems.push(next);
        }
      } else if (action === "decrement") {
        nextSelectedItems = selectedItems.slice(0, -1);
      }

      if (type === "change-plan") {
        setCount(nextSelectedItems.length + usedIdsByType.length);
      } else {
        setCount(nextSelectedItems.length);
      }

      const newGroup =
        nextSelectedItems.length > 0
          ? {
              slug: addonType,
              type: addonType,
              id: nextSelectedItems.map((a) => a.id),
              items: nextSelectedItems,
              count: nextSelectedItems.length,
              name: nextSelectedItems[0]?.name,
              gross_amount: nextSelectedItems.reduce(
                (sum, a) => sum + (a.gross_amount || 0),
                0,
              ),
              amount: nextSelectedItems.reduce(
                (sum, a) => sum + (a.amount || 0),
                0,
              ),
            }
          : null;

      return newGroup ? [...others, newGroup] : [...others];
    });
  }

  function handleExtraTeamMembers(action) {
    handleAddonChange(
      "users-v2-1",
      action,
      availableV2Ids,
      setExtraTeamMembers,
    );
  }

  function matchesGroup(item, group) {
    if (group === "Webhooks") return item?.slug?.includes("webhook");
    if (group === "Call recording storage")
      return item?.slug?.includes("recording");
    if (group === "AI Sale Agent") return item?.slug === AI_SALE_AGENT_SLUG;
    if (group === "AI Receptionist")
      return item?.slug === AI_RECEPTIONIST_AGENT_SLUG;
    return false;
  }

  function handleAutomations(action) {
    handleAddonChange(
      "automation-v2-pack-of-5",
      action,
      availableV2Ids,
      setExtraAutomations,
    );
  }

  function handleLeadStorage(action) {
    handleAddonChange(
      "lead-storage-extra-10k",
      action,
      availableV2Ids,
      setExtraLeadStorage,
    );
  }

  return (
    <div style={{ flexGrow: 1, overflowY: "auto" }}>
      {/* addons */}
      <div
        style={{
          padding: "12px 2px",
          display: "flex",
          flexDirection: "column",
          gap: 24,
          width: "100%",
        }}
      >
        <div>
          {/* show addons available */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              width: "100%",
            }}
          >
            {availableAddons?.length === 0 && (
              <div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 400,
                    textAlign: "center",
                    color: "#7367f0",
                  }}
                >
                  No add-ons available
                </div>
              </div>
            )}
            {/* unit add-ons: one card each, bought as add-on × quantity */}
            {unitAddons.map((addon) => {
              const perUnit = Number(addon.feature_value) || 1;
              const qty = unitQuantity(addon);
              return (
                <QuantityAddonCard
                  key={addon.id}
                  icon="🧑‍🤝‍🧑"
                  title={`${addon.name}${perUnit > 1 ? ` (${perUnit} each)` : ""}${
                    qty > 0 && perUnit > 1 ? ` · ${qty * perUnit} total` : ""
                  }`}
                  price={addon.gross_amount}
                  priceSuffix={perUnit > 1 ? "/pack /month" : "/month each"}
                  value={qty}
                  onIncrement={() => changeUnitQuantity(addon, 1)}
                  onDecrement={() => changeUnitQuantity(addon, -1)}
                  currencySymbol={currencySymbol}
                />
              );
            })}
            {/* Voice API: pick the duration, then the quantity */}
            {supportsQuantity &&
              VOICE_API_SLUGS.map(({ slug, title, unit }) => {
                const rows = (plan?.addons || []).filter((a) => a.slug === slug);
                if (rows.length === 0) return null;
                const row =
                  rows.find((a) => a.id === voiceApiCycle[slug]) || rows[0];
                return (
                  <div key={slug}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      {rows.map((r) => (
                        <Button
                          key={r.id}
                          size="sm"
                          color="primary"
                          outline={r.id !== row.id}
                          disabled={
                            remainingDays != null &&
                            remainingDays < cycleMonths(r) * 30
                          }
                          onClick={() => pickVoiceApiCycle(slug, r)}
                        >
                          {cycleMonths(r)} months
                        </Button>
                      ))}
                    </div>
                    <QuantityAddonCard
                      icon="📞"
                      title={title}
                      price={row.gross_amount / cycleMonths(row)}
                      priceSuffix={`/${unit} /month`}
                      value={unitQuantity(row)}
                      onIncrement={() => changeUnitQuantity(row, 1)}
                      onDecrement={() => changeUnitQuantity(row, -1)}
                      currencySymbol={currencySymbol}
                    />
                  </div>
                );
              })}
            {/* custom extra members (old one-row-per-user add-on; only when the plan has no unit add-on) */}
            {availableUserV2Ids.length > 0 && !hasUnitFor(availableUserV2Ids) && (
              <QuantityAddonCard
                icon="🧑‍🤝‍🧑"
                title="Extra team member"
                price={availableUserV2Ids[0]?.gross_amount}
                priceSuffix="/user /month"
                value={extraTeamMembers}
                onIncrement={() => handleExtraTeamMembers("increment")}
                onDecrement={() => handleExtraTeamMembers("decrement")}
                currencySymbol={currencySymbol}
              />
            )}
            {/* automations */}
            {availableAutomationV2Ids.length > 0 && !hasUnitFor(availableAutomationV2Ids) && (
              <QuantityAddonCard
                icon="🧑‍🤝‍🧑"
                title="Automation (Pack of 5 automations)"
                price={availableAutomationV2Ids[0]?.gross_amount}
                priceSuffix="/month"
                value={extraAutomations}
                onIncrement={() => handleAutomations("increment")}
                onDecrement={() => handleAutomations("decrement")}
                currencySymbol={currencySymbol}
              />
            )}
            {/* lead storage */}
            {availableLeadStorageIds.length > 0 && !hasUnitFor(availableLeadStorageIds) && (
              <QuantityAddonCard
                icon="🗄️"
                title="Lead storage (Pack of 10K leads)"
                price={availableLeadStorageIds[0]?.gross_amount}
                priceSuffix="/month"
                value={extraLeadStorage}
                onIncrement={() => handleLeadStorage("increment")}
                onDecrement={() => handleLeadStorage("decrement")}
                currencySymbol={currencySymbol}
              />
            )}
            {filteredAddons?.map((addon, index) => {
              const isWebhooks = addon.slug?.includes("webhook");
              const isRecording = addon.slug?.includes("recording");
              const isAIAgentSale = addon?.slug === AI_SALE_AGENT_SLUG;
              const isAIAgentReceptionist =
                addon?.slug === AI_RECEPTIONIST_AGENT_SLUG;
              const isCycleDisabled = isAIAgentCycleDisabled(
                addon,
                remainingDays,
              );
              const cycleMinDays = getUpfrontBillingDays(addon);

              const groupName = isWebhooks
                ? "Webhooks"
                : isRecording
                  ? "Call recording storage"
                  : isAIAgentSale
                    ? "AI Sale Agent"
                    : isAIAgentReceptionist
                      ? "AI Receptionist"
                      : null;

              const isGrouped = !!groupName;
              const showGroupHeader =
                isGrouped && !renderedGroups.has(groupName);
              const isSelected = selectedAddons?.some((a) => a.id === addon.id);
              const isActive = activeAddons?.some((a) => a.id === addon.id);

              if (showGroupHeader) {
                renderedGroups.add(groupName);
              }

              return (
                <React.Fragment key={addon.id}>
                  {/* Start a new group container if group starts here */}
                  {showGroupHeader && (
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e9ecef",
                        borderRadius: 12,
                        padding: "12px 16px",
                        marginBottom: 16,
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 16,
                          marginBottom: 8,
                        }}
                      >
                        {groupName}
                      </div>

                      {/* Already-purchased variant of this group, if any */}
                      {activeAddons
                        ?.filter(
                          (a) =>
                            matchesGroup(a, groupName) &&
                            !supersededAiAddonIds.includes(a.id),
                        )
                        .map((a) => (
                          <div
                            key={`active-${a.id}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              fontSize: 13,
                              color: "#495057",
                              marginBottom: 8,
                            }}
                          >
                            <span>
                              {isExpiredAiAddon(a) ? "Expired" : "Active"}:{" "}
                              {a.name}
                            </span>
                            <span
                              style={{
                                background: isExpiredAiAddon(a)
                                  ? "#fdecea"
                                  : "#e7f1ff",
                                color: isExpiredAiAddon(a)
                                  ? "#b42318"
                                  : "#0d6efd",
                                borderRadius: 999,
                                padding: "2px 8px",
                                fontSize: 12,
                                fontWeight: 500,
                              }}
                            >
                              {getBillingCycleName(a)}
                              {getAddonValidTill(a)
                                ? ` · till ${new Date(
                                    getAddonValidTill(a),
                                  ).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  })}`
                                : ""}
                            </span>
                          </div>
                        ))}

                      {/* Render grouped items below this header */}
                      {filteredAddons
                        .filter((a) => matchesGroup(a, groupName))
                        .map((item, idx, arr) => {
                          const isItemCycleDisabled = isAIAgentCycleDisabled(
                            item,
                            remainingDays,
                          );
                          const itemCycleMinDays = getUpfrontBillingDays(item);
                          const isRecordingItem =
                            item.slug?.includes("recording");
                          // A renewable AI add-on is still in activeAddons, but must
                          // behave like an unheld one here: selectable, unchecked,
                          // and not greyed out — that is what makes it renewable.
                          const isItemRenewableAi = renewableAiAddonIds.includes(
                            item.id,
                          );
                          const isItemHeld =
                            !isItemRenewableAi &&
                            activeAddons?.some((a) => a.id === item.id);
                          const isItemSelected = isRecordingItem
                            ? selectedRecordingId === item.id || isItemHeld
                            : selectedAddons?.some((a) => a.id === item.id) ||
                              isItemHeld;

                          const isItemActive = isItemHeld;

                          return (
                            <label
                              key={item.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "12px 0",
                                cursor:
                                  isItemActive 
                                    ? "not-allowed"
                                    : "pointer",
                                opacity:
                                  isItemActive 
                                    ? 0.6
                                    : 1,
                                borderBottom:
                                  idx < arr.length - 1
                                    ? "1px solid #e9ecef"
                                    : "none",
                              }}
                              onClick={() => {
                                // if (isItemCycleDisabled) return;
                                const isSameRecording =
                                  selectedRecordingId === item.id;

                                if (isRecordingItem) {
                                  if (isSameRecording) {
                                    setSelectedRecordingId(null);
                                    handleAddon({ ...item, deselect: true });
                                  } else {
                                    if (selectedRecordingId) {
                                      const prev = selectedAddons.find(
                                        (a) => a.id === selectedRecordingId,
                                      );
                                      if (prev)
                                        handleAddon({
                                          ...prev,
                                          deselect: true,
                                        });
                                    }

                                    setSelectedRecordingId(item.id);
                                    handleAddon(item);
                                  }
                                } else {
                                  if (
                                    groupName === "AI Sale Agent" ||
                                    groupName === "AI Receptionist"
                                  ) {
                                    handleAIAgentAddonClick(item);
                                  } else {
                                    handleAddon(item);
                                  }
                                }
                              }}
                            >
                              {/* Styled checkbox (even for recording) */}
                              <span
                                style={{
                                  height: 20,
                                  width: 20,
                                  borderRadius: "50%",
                                  border: "2px solid #ccc",
                                  position: "relative",
                                  background: isItemSelected
                                    ? "#0d6efd"
                                    : "#fff",
                                }}
                              >
                                {isItemSelected && (
                                  <span
                                    style={{
                                      position: "absolute",
                                      top: 3,
                                      left: 3,
                                      width: 10,
                                      height: 10,
                                      borderRadius: "50%",
                                      background: "#fff",
                                    }}
                                  />
                                )}
                              </span>

                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <span style={{ fontWeight: 500 }}>
                                    {item.name}
                                  </span>
                                  {isItemRenewableAi && (
                                    <span
                                      style={{
                                        background: activeAddons?.some(
                                          (a) => a.id === item.id && isExpiredAiAddon(a),
                                        )
                                          ? "#fdecea"
                                          : "#e7f1ff",
                                        color: activeAddons?.some(
                                          (a) => a.id === item.id && isExpiredAiAddon(a),
                                        )
                                          ? "#b42318"
                                          : "#0d6efd",
                                        borderRadius: 999,
                                        padding: "2px 8px",
                                        fontSize: 12,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {activeAddons?.some(
                                        (a) => a.id === item.id && isExpiredAiAddon(a),
                                      )
                                        ? "Expired — renew"
                                        : "Current — renew"}
                                    </span>
                                  )}
                                </div>
                                <div style={{ color: "#495057", fontSize: 14 }}>
                                  {currencySymbol} <strong>{item.gross_amount / 100}</strong>{" "}
                                  {getBillingCycleLabel(item)}
                                </div>
                                {(groupName === "AI Sale Agent" ||
                                  groupName === "AI Receptionist") &&
                                  isItemCycleDisabled && (
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: "#FF0000",
                                        marginTop: 4,
                                      }}
                                    >
                                    Requires {itemCycleMinDays}+ days of plan validity. If added on the customer's request, adjust the amount accordingly and verify the add-on's validity after purchase.
                                    </div>
                                  )}
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  )}

                  {/* Render ungrouped item normally */}
                  {!isGrouped && (
                    <div
                      style={{
                        background: "#fff",
                        border: "1px solid #e9ecef",
                        borderRadius: 12,
                        padding: "16px 12px",
                        marginBottom: 12,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        maxWidth: 500,
                        opacity: isCycleDisabled ? 0.7 : 1,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 500 }}>{addon.name}</div>
                        <div style={{ color: "#495057", marginTop: 4 }}>
                          {currencySymbol} <strong>{addon.gross_amount / 100}</strong>{" "}
                          {getBillingCycleLabel(addon)}
                        </div>
                        {isCycleDisabled && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#856404",
                              marginTop: 4,
                            }}
                          >
                            Requires {cycleMinDays}+ days left on plan
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() =>
                          !isCycleDisabled && handleAddon(addon)
                        }
                        disabled={isActive || isCycleDisabled}
                        style={{
                          background:
                            isActive || isSelected
                              ? "#4CAF50"
                              : isCycleDisabled
                                ? "#adb5bd"
                                : "#0d6efd",
                          color:
                            isActive || isCycleDisabled
                              ? "#6c757d"
                              : "#fff",
                          border: "none",
                          borderRadius: 20,
                          padding: "6px 16px",
                          fontWeight: 500,
                          cursor:
                            isActive || isSelected || isCycleDisabled
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {isActive
                          ? "Purchased"
                          : isCycleDisabled
                            ? `Need ${cycleMinDays}+ days`
                            : isSelected
                              ? "Selected"
                              : "Add"}
                      </button>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddonsCommon;
